import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { isRequestError } from '@utils/requests';

import { Loading } from 'components/Loading';
import { useStore } from 'models/Provider';

import { OneCRMConfig, loadOneCRMSettings, saveOneCRMSettings, updateOneCRMSettings } from './api';

export function OneCRMSettingsPage() {
  const settingsQuery = useQuery({
    queryKey: ['onecrm-settings'],
    queryFn: loadOneCRMSettings,
  });

  if (settingsQuery.isLoading || settingsQuery.data === undefined) return <Loading delay={500} />;

  return (
    <div>
      <Stack space={4}>
        <Text font="heading2" text="OneCRM" />
        <Separator />

        {settingsQuery.data === null ? (
          <CreateSettings />
        ) : (
          <EditSettings config={settingsQuery.data} />
        )}
      </Stack>
    </div>
  );
}

function useOneCRMForm(config?: OneCRMConfig) {
  const { t } = useStore();

  const [state, setState] = useState<Record<keyof OneCRMConfig, string>>({
    applicationTemplate: config?.applicationTemplate ?? '',
    jobTemplate: config?.jobTemplate ?? '',
    onboardingTemplate: config?.onboardingTemplate ?? '',
    password: '',
    url: config?.url ?? '',
    idNumber: config?.idNumber ?? '',
  });

  const [errors, setErrors] = useState<Record<keyof OneCRMConfig, string>>({
    applicationTemplate: '',
    jobTemplate: '',
    onboardingTemplate: '',
    password: '',
    url: '',
    idNumber: '',
  });

  return {
    setState,
    setErrors,
    resetErrors: () => {
      setErrors({
        applicationTemplate: '',
        jobTemplate: '',
        onboardingTemplate: '',
        password: '',
        url: '',
        idNumber: '',
      });
    },
    errors,
    state,
    url: {
      type: 'text',
      label: t('integrations.onecrm.settings.url'),
      defaultValue: state.url,
      onChange: (value: string) => {
        setState((old) => ({ ...old, url: value }));
        setErrors((old) => ({ ...old, url: '' }));
      },
      error: errors.url,
    },
    idNumber: {
      type: 'text',
      label: t('integrations.onecrm.settings.idNumber'),
      defaultValue: state.idNumber,
      onChange: (value: string) => {
        setState((old) => ({ ...old, idNumber: value }));
        setErrors((old) => ({ ...old, idNumber: '' }));
      },
      error: errors.idNumber,
    },
    password: {
      type: 'password',
      label: t('integrations.onecrm.settings.password'),
      placeholder: config ? '**********' : '',
      onChange: (value: string) => {
        setState((old) => ({ ...old, password: value }));
        setErrors((old) => ({ ...old, password: '' }));
      },
      error: errors.password,
    },
    jobTemplate: {
      type: 'text',
      label: t('integrations.onecrm.settings.jobTemplate'),
      defaultValue: state.jobTemplate,
      onChange: (value: string) => {
        setState((old) => ({ ...old, jobTemplate: value }));
        setErrors((old) => ({ ...old, jobTemplate: '' }));
      },
      error: errors.jobTemplate,
    },
    applicationTemplate: {
      type: 'text',
      label: t('integrations.onecrm.settings.applicationTemplate'),
      defaultValue: state.applicationTemplate,
      onChange: (value: string) => {
        setState((old) => ({ ...old, applicationTemplate: value }));
        setErrors((old) => ({ ...old, applicationTemplate: '' }));
      },
      error: errors.applicationTemplate,
    },
    onboardingTemplate: {
      type: 'text',
      label: t('integrations.onecrm.settings.onboardingTemplate'),
      defaultValue: state.onboardingTemplate,
      onChange: (value: string) => {
        setState((old) => ({ ...old, onboardingTemplate: value }));
        setErrors((old) => ({ ...old, onboardingTemplate: '' }));
      },
      error: errors.onboardingTemplate,
    },
  };
}

function CreateSettings() {
  const queryClient = useQueryClient();
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();

  const formState = useOneCRMForm();

  const settingsMutation = useMutation(saveOneCRMSettings, {
    onSuccess() {
      showSuccess(t('integrations.onecrm.settings.create.success'));
      formState.resetErrors();
      queryClient.invalidateQueries(['onecrm-settings']);
    },
    onError(error) {
      if (isRequestError(error)) {
        showError(t('integrations.onecrm.settings.create.error'));
        formState.setErrors((old) => ({ ...old, ...error.response?.data }));
      }
    },
  });

  return (
    <Stack space={4}>
      {settingsMutation.isLoading && <Loading />}
      <Stack space={2}>
        <TextField {...formState.url} />
        <TextField {...formState.idNumber} />
        <TextField {...formState.password} />
        <TextField {...formState.jobTemplate} />
        <TextField {...formState.applicationTemplate} />
        <TextField {...formState.onboardingTemplate} />
      </Stack>
      <Stack align="flex-end" space={2}>
        <Button
          isDisabled={settingsMutation.isLoading}
          variant="primary"
          text={t('integrations.onecrm.settings.connect')}
          onClick={() => {
            settingsMutation.mutate(formState.state);
          }}
        />
      </Stack>
    </Stack>
  );
}

function EditSettings({ config }: { config: OneCRMConfig }) {
  const queryClient = useQueryClient();
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();

  const formState = useOneCRMForm(config);

  const settingsMutation = useMutation(updateOneCRMSettings, {
    onSuccess() {
      formState.resetErrors();
      showSuccess(t('integrations.onecrm.settings.update.success'));
      queryClient.invalidateQueries(['onecrm-settings']);
    },
    onError(error) {
      showError(t('integrations.onecrm.settings.update.error'));
      if (isRequestError(error)) {
        formState.setErrors((old) => ({
          ...old,
          ...error.response?.data,
        }));
      }
    },
  });

  return (
    <Stack space={4}>
      <Stack space={2}>
        <TextField {...formState.url} />
        <TextField {...formState.idNumber} />
        <TextField {...formState.password} />
        <TextField {...formState.jobTemplate} />
        <TextField {...formState.applicationTemplate} />
        <TextField {...formState.onboardingTemplate} />
      </Stack>
      <Stack align="flex-end" space={2}>
        <Button
          variant="primary"
          text={t('integrations.onecrm.settings.save')}
          isDisabled={
            (config.url === formState.state.url &&
              config.applicationTemplate === formState.state.applicationTemplate &&
              config.jobTemplate === formState.state.jobTemplate &&
              config.onboardingTemplate === formState.state.onboardingTemplate &&
              config.idNumber === formState.state.idNumber &&
              !formState.state.password) ||
            settingsMutation.isLoading
          }
          onClick={() => {
            settingsMutation.mutate({
              url: formState.state.url,
              jobTemplate: formState.state.jobTemplate,
              applicationTemplate: formState.state.applicationTemplate,
              onboardingTemplate: formState.state.onboardingTemplate,
              password: formState.state.password === '' ? undefined : formState.state.password,
              idNumber: formState.state.idNumber,
            });
          }}
        />
      </Stack>
    </Stack>
  );
}
